import { RISK_META, RISK_FALLBACK, TIER_BADGE, pocColor, pocInitials } from "@/lib/risk";

export function RiskBadge({ risk }) {
  const m = RISK_META[risk] || RISK_FALLBACK;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap ${m.badge}`}
      data-testid={`risk-badge-${m.key}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

export function TierBadge({ tier }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${
        TIER_BADGE[tier] || "bg-gray-100 text-gray-700 border-gray-200"
      }`}
      data-testid={`tier-badge-${tier}`}
    >
      {tier}
    </span>
  );
}

/**
 * Single-avatar chip. By default renders as an icon-only circle with the
 * full name in a tooltip. Pass `showName` for the legacy avatar+name layout
 * (used inside Firm 360 headers where there's room for the full name).
 *
 * Set `variant="sales"` to differentiate sales POC chips in tables — uses
 * a ring around the avatar so it reads "secondary" at a glance.
 */
export function PocChip({ name, showName = false, variant = "support" }) {
  if (!name) {
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-300 border border-dashed border-gray-200 text-[10px]"
        title="No POC assigned"
        data-testid="poc-chip-empty"
      >
        —
      </span>
    );
  }
  const isSales = variant === "sales";
  const ring = isSales ? "ring-2 ring-offset-1 ring-violet-300" : "";
  return (
    <span
      className="inline-flex items-center gap-2"
      title={isSales ? `${name} (Sales)` : name}
      data-testid={`poc-chip-${variant}-${name}`}
    >
      <span
        className={`w-7 h-7 rounded-full text-[10px] font-semibold flex items-center justify-center ${pocColor(
          name
        )} ${ring}`}
      >
        {pocInitials(name)}
      </span>
      {showName ? <span className="text-sm text-gray-700">{name}</span> : null}
    </span>
  );
}

/**
 * Renders support POC + sales POC avatars side-by-side. Used in every list
 * table row. Avatars are icon-only; full name surfaces in the tooltip on hover.
 */
export function PocChipPair({ poc, salesPoc }) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="poc-pair">
      <PocChip name={poc} />
      <PocChip name={salesPoc} variant="sales" />
    </span>
  );
}

/**
 * Plain-text rendering of `num_users` for table cells. Matches the visual
 * weight of "Last seen" / "City" so the column doesn't feel heavier than its
 * neighbours.
 *   "-"            → muted em-dash
 *   "unlimited"    → "Unlimited"
 *   anything else  → "{value} users"
 */
export function UsersCell({ value }) {
  const v = value ?? "-";
  if (v === "-" || v === "" || v === null) {
    return (
      <span className="text-gray-400 text-xs" data-testid="users-cell-empty">—</span>
    );
  }
  if (String(v).toLowerCase() === "unlimited") {
    return (
      <span className="text-sm text-gray-700" data-testid="users-cell-unlimited">
        Unlimited
      </span>
    );
  }
  return (
    <span className="text-sm text-gray-700 tabular-nums" data-testid={`users-cell-${v}`}>
      {v} users
    </span>
  );
}

/**
 * Pill rendering of `num_users` — used in the Firm 360 header where the value
 * sits alongside the Tier / Risk / POC badges and needs to feel like a peer.
 */
export function UsersBadge({ value }) {
  const v = value ?? "-";
  if (v === "-" || v === "" || v === null) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-gray-100 text-gray-500 border border-gray-200 px-2 py-0.5 text-[11px] font-medium"
        data-testid="users-badge-empty"
      >
        — users
      </span>
    );
  }
  if (String(v).toLowerCase() === "unlimited") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
        data-testid="users-badge-unlimited"
      >
        ∞ Unlimited users
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 text-[11px] font-medium tabular-nums whitespace-nowrap"
      data-testid={`users-badge-${v}`}
    >
      {v} users
    </span>
  );
}

